// src/components/IOSCompatibleInput.tsx
import React, { forwardRef, useState, useEffect, useRef, MutableRefObject } from 'react';
import { useViewportContext } from '../../context/ViewportContext';
import { scrollToInput } from '../../utils/scrollUtils';
import { Input } from './IOSCompatibleInput.style';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    errorMessage?: string;
    hasError?: boolean;
  }
  
  const IOSCompatibleInput = forwardRef<HTMLInputElement, InputProps>(
    ({ label, errorMessage, hasError, onFocus, onBlur, ...rest }, ref) => {
      const { isKeyboardVisible } = useViewportContext();
      const [isFocused, setIsFocused] = useState(false);
      const inputRef = useRef<HTMLInputElement>(null) as MutableRefObject<HTMLInputElement | null>;
  
      const combinedRef = (node: HTMLInputElement) => {
        inputRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as MutableRefObject<HTMLInputElement | null>).current = node;
        }
      };
  
      const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        if (onFocus) onFocus(e);
        setTimeout(() => {
          scrollToInput(inputRef.current);
        }, 300);
      };
  
      const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        if (onBlur) onBlur(e);
      };
  
      useEffect(() => {
        if (isKeyboardVisible && isFocused && inputRef.current) {
          scrollToInput(inputRef.current);
        }
      }, [isKeyboardVisible, isFocused]);
  
      return (
        <div className={`ios-input-container ${isFocused ? 'input-focused' : ''}`}>
          {label && <label className="ios-input-label">{label}</label>}
          <Input
            ref={combinedRef}
            className={`ios-input ${hasError ? 'ios-input-error' : ''}`}
            onFocus={handleFocus}
            onBlur={handleBlur}
            autoComplete="nope"
            autoCorrect="off"
            spellCheck="false"
            {...rest}
          />
          {errorMessage && <div className="ios-input-error-message">{errorMessage}</div>}
        </div>
      );
    }
  );
  
  IOSCompatibleInput.displayName = 'IOSCompatibleInput';
  
  export default IOSCompatibleInput;